import _ from "lodash";
import { AI_TRANSLATION_LANGUAGES } from "@toddle-edu/common-utils";

import client from "lib/apolloClient/client";
import { updateSettings } from "modules/ConfigurationThunks";
import { loadLocalesBundle } from "Utils";
import ACLStore from "lib/aclStore";
import {
  goToPlatformHomeChildRoute,
  goToLearningCourseChildRoute,
} from "modules/NavigationModule";
import {
  getCurrentCurriculumContextForJumpTo,
  getIsFutureAcademicYearSelected,
  getIsStaff,
} from "modules/CommonSelectors";
import { setupCurriculumProgramDependencies } from "Platform/modules/PlatformModule";
import { getSelectedLearningCourseId } from "LearningCourse/modules/Utils";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";

const _getHomepageContentVisibilityStatus = ({
  userCoursesCount,
  projectGroups,
}) => {
  return (dispatch, getState) => {
    const state = getState();

    const canShowProjectGroupsInFutureAY = ACLStore.can(
      "FeatureFlag:ShowProjectsInFutureAY"
    );

    const isFutureAcademicYearSelected = getIsFutureAcademicYearSelected(state);

    const activeTab = _.get(state, "login.activeTab", "");
    const isAdminPage = activeTab === "admin";

    const hideProjectGroupsForFutureAY =
      isFutureAcademicYearSelected && !canShowProjectGroupsInFutureAY;

    return isAdminPage ||
      (_.isEqual(userCoursesCount, 0) &&
        (_.isEmpty(projectGroups) || hideProjectGroupsForFutureAY))
      ? "NOT_VISIBLE"
      : "VISIBLE";
  };
};

export const getRocketIconVisibilityStatus = ({
  userCoursesCount,
  projectGroups,
}) => {
  /**
   * When there is nothing on hompage, we don't need to show Rocket Icon as there are no features to navigate to.
   * Similarly, for free curriculum programs, there are very less features so, we don't need to show Rocket Icon.
   *
   */
  return (dispatch, getState) => {
    return dispatch(
      _getHomepageContentVisibilityStatus({ userCoursesCount, projectGroups })
    ) === "NOT_VISIBLE"
      ? "NOT_VISIBLE"
      : "VISIBLE";
  };
};

export const goToLearningCourseRoute = ({
  learningCourseId,
  isOpenInNewTab = false,
}) => {
  return (dispatch, getState) => {
    const state = getState();
    const selectedLearningCourseId = getSelectedLearningCourseId(state);
    const platformCurriculumContext = _.get(
      state,
      "platform.currentCurriculumProgram"
    );

    const platformCurriculumId = _.get(platformCurriculumContext, "id");

    let curriculumProgramId = platformCurriculumId;

    if (selectedLearningCourseId == learningCourseId) return;
    if (ACLStore.can("FeatureFlag:EnableGlobalSearchJumpToRevamp")) {
      // User can go to cross curriculum courses from jumpTo section inside global search modal
      const globalSearchCurriculumContext = getCurrentCurriculumContextForJumpTo(
        state
      );

      const globalSearchCurriculumId = _.get(
        globalSearchCurriculumContext,
        "id"
      );

      if (
        globalSearchCurriculumContext &&
        platformCurriculumId != globalSearchCurriculumId
      ) {
        curriculumProgramId = globalSearchCurriculumId;
        dispatch(
          setupCurriculumProgramDependencies({
            curriculumProgramIdPath: globalSearchCurriculumId,
          })
        );
      }
    }

    return dispatch(
      goToLearningCourseChildRoute({
        learningCourseId,
        curriculumProgramId,
        isOpenInNewTab,
      })
    );
  };
};

export const goToCourseRoute = ({ courseId, learningCourseId }) => {
  return (dispatch, getState) => {
    const state = getState();
    const selectedCourse = _.get(
      state,
      "teacher.selected_class.selected_course"
    );
    const platformCurriculumContext = _.get(
      state,
      "platform.currentCurriculumProgram"
    );
    const platformCurriculumId = _.get(platformCurriculumContext, "id");

    let curriculumProgramId = platformCurriculumId;

    if (selectedCourse == courseId) return;
    if (ACLStore.can("FeatureFlag:EnableGlobalSearchJumpToRevamp")) {
      // User can go to cross curriculum courses from jumpTo section inside global search modal
      const globalSearchCurriculumContext = getCurrentCurriculumContextForJumpTo(
        state
      );

      const globalSearchCurriculumId = _.get(
        globalSearchCurriculumContext,
        "id"
      );

      if (
        globalSearchCurriculumContext &&
        platformCurriculumId != globalSearchCurriculumId
      ) {
        curriculumProgramId = globalSearchCurriculumId;
        dispatch(
          setupCurriculumProgramDependencies({
            curriculumProgramIdPath: globalSearchCurriculumId,
          })
        );
      }
    }

    const isLearningCoursesEnabled = getIsLearningCoursesEnabled({
      curriculumProgramId,
    });

    if (isLearningCoursesEnabled) {
      dispatch(
        goToLearningCourseChildRoute({
          curriculumProgramId,
          learningCourseId,
          classId: courseId,
        })
      );
    } else {
      const route = `courses/${courseId}`;
      return dispatch(goToPlatformHomeChildRoute({ route }));
    }
  };
};

export const changeLocalesLocally = ({
  value,
  userId,
  i18n,
  showToastMsg = true,
}) => {
  return async (dispatch, getState) => {
    const settings = [{ name: "locale", value }];

    const isStaff = getIsStaff(getState());

    // when staff changes the platform language then update the AI voice language too
    if (isStaff) {
      const languageId = _.toString(
        _.find(
          AI_TRANSLATION_LANGUAGES,
          language => _.get(language, "code") === value
        )?.id
      );

      if (languageId) {
        settings.push(
          {
            name: "ConfigureProgressReportAIVoiceLanguage",
            value: languageId,
          },
          {
            name: "ConfigureAssessmentAIVoiceLanguage",
            value: languageId,
          },
          {
            name: "ConfigureRTEAIVoiceLanguage",
            value: languageId,
          }
        );
      }
    }

    dispatch(updateSettings({ settings, showToastMsg }));

    await loadLocalesBundle(value);
    i18n.changeLanguage(value);
    if (ACLStore.can("FeatureFlag:RefetchQueriesOnLanguageChange")) {
      setTimeout(() => {
        client.reFetchObservableQueries(true);
      });
    }
  };
};
